/**
 * German display overlays — promoted from apps/web-customer/src/i18n/locales/de/
 * so BOTH the customer app and the public catalog feed (`i18n.de`, consumed by
 * the marketing website's pricing rebuild) share one copy.
 *
 * Same load-bearing rule as the overlays that consume this: @fd/pricing stays
 * canonical English (engine outputs, persisted display strings, English PDFs);
 * German is a render-time overlay keyed by STRUCTURAL identifiers (family, plan
 * key, kind, tier token, unit). Any missing DE key degrades to English.
 *
 * Plan `features`/`exclusions` are POSITIONAL translations of the published
 * snapshot's English arrays (catalog_plan.features_json order). If an admin
 * edits the English bullets so the lengths drift, consumers must fall back to
 * English PER BULLET — never shift the remaining translations.
 *
 * A TS module (not JSON) so it loads identically in the Worker, Vite, vitest,
 * and `node --experimental-strip-types` without JSON-import attributes.
 */

export interface DePlanCopy {
  name?: string;
  subtitle?: string;
  features?: string[];
  exclusions?: string[];
  /** Scenario-validation extras (plan_key scout_scenario_validation only). */
  duration?: string;
  scope?: string;
  includes?: string;
  transition?: string;
}

export const DE_PRICING = {
  family: {
    live: {
      name: "Live",
      tagline: "Für Veranstalter & Kulturorganisationen",
      description:
        "Für Veranstalter und Kulturorganisationen — Nachfrageprognosen, Kampagnen, CRM und Analysen. Umfasst die Apps Lookout, Wave, Backhaul und Sentinel.",
    },
    prisma: {
      name: "Prisma",
      tagline: "Für Marketing-Teams & Agenturen",
      description:
        "Eine Plattform, die Kunden- und Vor-Ort-Verhalten automatisch in absichtsbasierte Zielgruppen für Ihre Paid-Social-Kampagnen verwandelt — KI-Kampagnenautomatisierung, Audience Intelligence und Multi-Mandanten-Verwaltung.",
    },
    affinity: {
      name: "Affinity",
      tagline: "Produktempfehlungs-API",
      description:
        "Affinity ist die Empfehlungs-API von Future Demand für Produkt- und Veranstaltungsempfehlungen.",
    },
  },
  app: {
    lookout: {
      name: "Lookout",
      description: "Prognostizieren Sie die Nachfrage nach Veranstaltungen und Programmen Monate im Voraus.",
    },
    wave: {
      name: "Wave",
      description: "Starten Sie hochkonvertierende Social-Media-Kampagnen in 15 Minuten.",
    },
    backhaul: {
      name: "Backhaul",
      description: "Senden Sie automatisch die richtige E-Mail an die richtige Person.",
    },
    sentinel: {
      name: "Sentinel",
      description: "Stellen Sie jede Frage zu Ihren Daten in einfacher Sprache.",
    },
  },
  // Keyed by (family → plan_key), incl. Scout tiers + scenario validation under
  // live. Names identical to English are omitted (EN fallback). Feature bullet
  // German sourced from the marketing site's DE pricing pages (the previous
  // hand-maintained translation), reordered where needed to match the snapshot's
  // English array order.
  plan: {
    live: {
      essentials: {
        features: [
          "Live Attribution & Insights",
          "Meta-Werbeplattform",
          "Bis zu 25.000 Tickets/Jahr",
          "1 Nutzer inklusive",
          "Support per Formular",
        ],
        exclusions: ["Live Location", "Data Integration", "Account Manager", "SLA"],
      },
      core: {
        features: [
          "Live Attribution, Location & Insights",
          "Meta-Werbeplattform",
          "Automatisierte Verkaufsdatenimporte",
          "2 Nutzer inklusive",
          "Telefon & E-Mail · Account Manager",
        ],
        exclusions: ["Backhaul", "Sentinel", "SLA"],
      },
      proGrowth: {
        subtitle: "Nur Kampagnen",
        features: [
          "Live Attribution, Location & Insights",
          "Meta-Werbeplattform",
          "2 Data-Integration-Quellen",
          "4 Nutzer inklusive",
          "Telefon & E-Mail · Account Manager · SLA",
          "Promoter Split: 250 €/Einheit",
        ],
        exclusions: ["Backhaul", "Sentinel"],
      },
      proEngage: {
        subtitle: "Nur CRM & Analytics",
        features: [
          "Live Attribution, Location & Insights",
          "Backhaul (E-Mail-Segmentierung)",
          "Sentinel (Natural Language Querying)",
          "2 Data-Integration-Quellen",
          "4 Nutzer inklusive",
          "Telefon & E-Mail · Account Manager · SLA",
        ],
        exclusions: ["Lookout", "Wave"],
      },
      pro360: {
        subtitle: "Komplettpaket",
        features: [
          "Live Attribution, Location & Insights",
          "Meta-Werbeplattform",
          "2 Data-Integration-Quellen",
          "Backhaul + Sentinel",
          "4 Nutzer inklusive",
          "Telefon & E-Mail · Account Manager · SLA",
          "Promoter Split: 250 €/Einheit",
        ],
        exclusions: [],
      },
      scout: {
        subtitle: "Einzeltournee oder kleiner Veranstalter",
        features: [
          "Bis zu 250.000 Tickets/Jahr inklusive",
          "Unbegrenzte Szenarien & Archiv",
          "1 Nutzer",
          "Self-serve Onboarding",
          "Support per Formular",
        ],
      },
      scoutPro: {
        subtitle: "Mehrere Tourneen oder mittelgroßer Veranstalter",
        features: [
          "Bis zu 1,5 Mio. Tickets/Jahr inklusive",
          "Unbegrenzte Szenarien & Archiv",
          "3 Nutzer",
          "Geführtes Onboarding · Quartalsreviews",
          "Telefon & E-Mail · Account Manager",
        ],
      },
      scoutEnt: {
        subtitle: "Nationaler oder internationaler Betreiber",
        features: [
          "Bis zu 5 Mio. Tickets/Jahr inklusive",
          "Unbegrenzte Szenarien & Archiv",
          "10 Nutzer",
          "Concierge-Onboarding · Monatliche Reviews",
          "Persönlicher Account Manager",
        ],
      },
      scout_scenario_validation: {
        subtitle:
          "Einmaliges Engagement für Tournee- & Saisonplanung — idealer Einstieg vor einem jährlichen Scout-Abonnement",
        duration: "6–8 Wochen",
        scope: "Bis zu 500 Prognosen, 10 Szenario-Konfigurationen",
        includes: "Concierge-Onboarding, Account Manager, 1 Überarbeitungsrunde",
        transition: "Voller Betrag wird auf das Scout-Abonnement im ersten Jahr angerechnet",
      },
    },
    prisma: {
      essentials: {
        subtitle: "Für kleine Teams, die mit KI-Kampagnen starten",
        features: [
          "Meta Ads Kampagnen",
          "Google Display",
          "Sentinel AI",
          "1 Nutzer-Seat",
          "Bis zu €5k/Jahr Ad Spend",
          "Standard-Support",
        ],
      },
      core: {
        subtitle: "Für wachsende Teams mit Multi-Channel-Kampagnen",
        features: [
          "Meta Ads Kampagnen",
          "Google Display (bald verfügbar)",
          "Sentinel AI",
          "1 Nutzer-Seat (+€199/zus.)",
          "Bis zu €2,5k/Mo Ad Spend",
          "Priority-Support",
        ],
      },
      coreplus: {
        name: "Core+",
        subtitle: "Für Agenturen, die mehrere Kunden skaliert verwalten",
        features: [
          "Meta Ads Kampagnen",
          "Google Display (bald verfügbar)",
          "Sentinel AI (ab €300)",
          "5 Nutzer-Seats (+€99/zus.)",
          "Unbegrenzter Ad Spend",
          "Dedizierter Account Manager",
        ],
      },
    },
    affinity: {
      core: {
        subtitle: "Für Produkte, die Empfehlungen in ihren bestehenden Stack integrieren",
      },
      pro: {
        subtitle: "Für Teams, die eigene Katalog- und Event-Daten ins Modell einspeisen möchten",
      },
      data_integration: {
        name: "Datenintegration",
        subtitle: "Individuelle Katalog- & Event-Datenanbindung",
      },
    },
  } as Record<string, Record<string, DePlanCopy>>,
  volume: {
    labels: [
      "bis zu 50k",
      "bis zu 200k",
      "bis zu 300k",
      "bis zu 400k",
      "bis zu 500k",
      "bis zu 550k",
      "bis zu 600k",
      "bis zu 650k",
      "700k+",
      "800k+",
      "900k+",
      "1 Mio.+",
      "bis zu 25k",
    ],
    essentialsCapacityLabel: "bis zu 25k",
  },
  interval: {
    annual: "Jährlich",
    quarterly: "Vierteljährlich",
    billingNote: {
      annual: "jährlich abgerechnet, im Voraus",
      quarterly: "vierteljährlich abgerechnet, im Voraus",
    },
  },
};

export const DE_ENTITLEMENTS = {
  unlimited: "Unbegrenzt",
  included: "Inklusive",
  notIncluded: "Nicht enthalten",
  perMonth: "/ Monat",
  perYear: "/ Jahr",
  kind: {
    event_capacity: "Jährliche Ticketkapazität",
    managed_ad_spend: "Verwaltetes Werbebudget",
    api_calls: "API-Anfragen",
    seats: "Nutzerplätze",
    credits: "Credits",
    cocreate_images: "CoCreate-Bilder", // legacy offer snapshots only
    data_integration_sources: "Data-Integration-Quellen",
    forecasts: "Prognosen",
    scenario_configs: "Szenario-Konfigurationen",
    refinement_rounds: "Verfeinerungsrunden",
    scenarios: "Szenarien",
    promoter_split: "Promoter Split",
    live_location: "Live-Location-Targeting",
    meta_ads: "Meta-Werbeplattform",
    google_display: "Google-Display-Kanal",
    sentinel_ai: "Sentinel-KI-Abfragen",
    auto_sales_imports: "Automatische Verkaufsdaten-Importe",
    data_integration: "Data Integration",
    support_tier: "Support",
    sla: "Service-Level-Agreement",
    account_manager: "Account Manager",
    onboarding_tier: "Onboarding",
    business_review_cadence: "Business Reviews",
  } as Record<string, string>,
  tierValue: {
    support_tier: {
      form: "Support per Formular",
      standard: "Standard-Support",
      phone_email: "Telefon- & E-Mail-Support",
      priority: "Priority-Support",
    },
    sla: { none: "Kein SLA", standard: "Standard-SLA" },
    account_manager: { none: "Keiner", shared: "Geteilt", dedicated: "Dediziert" },
    onboarding_tier: { self_serve: "Self-Service", guided: "Begleitet", concierge: "Concierge" },
    business_review_cadence: { none: "Keine", quarterly: "Quartalsweise", monthly: "Monatlich" },
  } as Record<string, Record<string, string>>,
  unit: {
    tickets: "Tickets",
    requests: "Anfragen",
    seats: "Plätze",
    sources: "Quellen",
    credits: "Credits",
    forecasts: "Prognosen",
    configs: "Konfigurationen",
    rounds: "Runden",
    scenarios: "Szenarien",
    entities: "Einheiten",
  } as Record<string, string>,
};

export type DePricingOverlay = typeof DE_PRICING;
export type DeEntitlementsOverlay = typeof DE_ENTITLEMENTS;
